import axios from 'axios';
import * as moment from 'moment';
import { URL_FOUNDATION } from '../../config/keys';
import { translateToString } from '../../styles/global/translate';

// The opening-hours check of an event: an hours column of the companies
// can carry a rule (set from its settings popup) that warns or blocks when
// a timed event falls outside the company's hours of that column. The
// server evaluates it; the popup asks while a date is picked, the calendar
// before applying a drop, and both colour the answer by its mode.

export type OpeningHoursConflict = {
	mode: 'warn' | 'block',
	column_id: number,
	column_name: string,
	day: string,
	// the column's open ranges on the event's day, minutes from midnight;
	// empty when the company is closed that day
	ranges: [number, number][],
};

export type OpeningHoursCheck = {
	client_company_id: number,
	event_type_id: number,
	// UTC, as the event is saved
	start_date: string,
	end_date: string,
	// the browser's offset at the event's start, minutes east of UTC
	utc_offset_minutes: number,
};

export async function checkOpeningHours(body: OpeningHoursCheck): Promise<OpeningHoursConflict[]> {
	return axios.post(`${URL_FOUNDATION}/api/v2/opening-hours-rules/check`, body).then(res => res.data);
}

/** The conflict to show: a block outranks a warn. */
export function worstOpeningHoursConflict(conflicts: OpeningHoursConflict[]): OpeningHoursConflict | undefined {
	return conflicts.find(conflict => conflict.mode === 'block') ?? conflicts[0];
}

export function openingHoursConflictMessage(conflict: OpeningHoursConflict): string {
	const time = (minutes: number) => moment.utc(minutes * 60 * 1000).format('HH:mm');
	return translateToString(conflict.ranges.length > 0 ? 'event.opening_hours_conflict' : 'event.opening_hours_conflict_closed')
		.replace('{{COLUMN}}', conflict.column_name)
		.replace('{{DAY}}', translateToString(`days.${conflict.day}`))
		.replace('{{RANGES}}', conflict.ranges.map(([open, close]) => `${time(open)}-${time(close)}`).join(', '));
}

/** The conflict behind a server refusal (400 with a structured body), if that is what the error is. */
export function openingHoursRefusal(error: unknown): OpeningHoursConflict | undefined {
	const raw = (error as { response?: { data?: unknown } } | undefined)?.response?.data;
	try {
		const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
		if (data && typeof data === 'object' && (data as { error?: string }).error === 'opening_hours_conflict') return data as OpeningHoursConflict;
	} catch {
		// not a structured refusal
	}
	return undefined;
}
